import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ArrowLeft, Edit2, MapPin, Building2, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { fetchHealthFacilityDetails, updateHealthFacility } from '../redux/HealthFacilitiesSlice';
import { formatDate } from '../utils/Config';

const ViewHealthFacility = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const [facility, setFacility] = useState({
    name: '',
    region: '',
    district: '',
    town: '',
    type: '',
    ownership: '',
    longitude: '',
    latitude: '',
    created_at: '',
    updated_at: ''
  });

  const [editedFacility, setEditedFacility] = useState({});

  const facilityTypes = ['Hospital', 'Clinic', 'CHPS'];
  const regions = [
    'Greater Accra',
    'Ashanti',
    'Western',
    'Eastern',
    'Central',
    'Northern',
    'Upper East',
    'Upper West',
    'Volta',
    'Bono'
  ];
  const ownershipTypes = ['Government', 'Private', 'CHAG'];

  useEffect(() => {
    const loadFacilityDetails = async () => {
      try {
        setLoading(true);
        const response = await dispatch(fetchHealthFacilityDetails(id)).unwrap();
        setFacility(response.data);
        setEditedFacility(response.data);
      } catch (error) {
        toast.error('Failed to load facility details');
        navigate('/dashboard/health-facilities');
      } finally {
        setLoading(false);
      }
    };

    loadFacilityDetails();
  }, [id, dispatch, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedFacility(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await dispatch(updateHealthFacility({ id, data: editedFacility })).unwrap();
      setFacility(editedFacility);
      setIsEditing(false);
      toast.success('Facility updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update facility');
    } finally {
      setSaving(false);
    }
  };

 

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/dashboard/health-facilities')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Back to Facilities
        </button>

        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 bg-[#1330C5] hover:opacity-90 text-white px-4 py-2 rounded-lg"
          >
            <Edit2 size={16} />
            Edit Facility
          </button>
        ) : (
          <div className="flex gap-3">
            <button
              onClick={() => {
                setIsEditing(false);
                setEditedFacility(facility);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-t-2 border-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="max-w-5xl">
        {/* Facility Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Building2 className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">{facility.name}</h1>
              <p className="text-gray-500 flex items-center gap-2">
                <MapPin size={16} />
                {facility.town}, {facility.district}, {facility.region}
              </p>
            </div>
          </div>
        </div>

        {/* Facility Details */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium border-b border-gray-200 pb-2">
                Basic Information
              </h2>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Facility Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="name"
                    value={editedFacility.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{facility.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Facility Type
                </label>
                {isEditing ? (
                  <select
                    name="type"
                    value={editedFacility.type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {facilityTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-900">{facility.type}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Ownership
                </label>
                {isEditing ? (
                  <select
                    name="ownership"
                    value={editedFacility.ownership}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {ownershipTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-900">{facility.ownership}</p>
                )}
              </div>
            </div>

            {/* Location Information */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium border-b border-gray-200 pb-2">
                Location Information
              </h2>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Region
                </label>
                {isEditing ? (
                  <select
                    name="region"
                    value={editedFacility.region}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {regions.map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-900">{facility.region}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  District
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="district"
                    value={editedFacility.district}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{facility.district}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Town
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="town"
                    value={editedFacility.town}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{facility.town}</p>
                )}
              </div>
            </div>

            {/* Coordinates */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium border-b border-gray-200 pb-2">
                GPS Coordinates
              </h2>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Longitude
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="longitude"
                    value={editedFacility.longitude}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{facility.longitude}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Latitude
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="latitude"
                    value={editedFacility.latitude}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{facility.latitude}</p>
                )}
              </div>
            </div>

            {/* Timestamps */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium border-b border-gray-200 pb-2">
                System Information
              </h2>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Created At
                </label>
                <p className="text-gray-900">{formatDate(facility.created_at)}</p>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Last Updated
                </label>
                <p className="text-gray-900">{formatDate(facility.updated_at)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewHealthFacility;